import equal from 'fast-deep-equal';

type UnknownBlock = { props?: Record<string, unknown>; children?: unknown[] };

function readSlot(block: unknown): string | undefined {
  const props = (block as UnknownBlock | null)?.props;
  const slot =
    props && typeof props === 'object' ? (props as Record<string, unknown>).slot : undefined;
  return typeof slot === 'string' ? slot : undefined;
}

function indexPropsBySlot(blocks: unknown[]): Map<string, Record<string, unknown>> {
  const bySlot = new Map<string, Record<string, unknown>>();

  const walk = (arr: unknown[]): void => {
    for (const block of arr) {
      if (!block || typeof block !== 'object') continue;
      const slot = readSlot(block);
      const props = (block as UnknownBlock).props;
      if (slot && props && typeof props === 'object') {
        bySlot.set(slot, props as Record<string, unknown>);
      }
      const children = (block as UnknownBlock).children;
      if (Array.isArray(children)) walk(children);
    }
  };

  walk(blocks);
  return bySlot;
}

// Layer a special-page proposal's changed slots onto current blocks, so siblings survive.
export function mergeSpecialPageDeltaBlocks({
  current,
  original,
  proposed,
  changedSlots,
}: {
  current: unknown[];
  original: unknown[];
  proposed: unknown[];
  changedSlots?: string[];
}): unknown[] {
  const originalBySlot = indexPropsBySlot(original);
  const proposedBySlot = indexPropsBySlot(proposed);

  const overrides = new Map<string, Record<string, unknown>>();
  for (const [slot, proposedProps] of proposedBySlot) {
    // A proposal that names its slots is authoritative; otherwise infer intent by diffing the baseline.
    const isChanged = changedSlots
      ? changedSlots.includes(slot)
      : !equal(proposedProps, originalBySlot.get(slot));
    if (isChanged) {
      overrides.set(slot, proposedProps);
    }
  }

  if (overrides.size === 0) return current;

  const applyOverrides = (blocks: unknown[]): unknown[] =>
    blocks.map((block) => {
      if (!block || typeof block !== 'object') return block;
      let next = block as UnknownBlock;
      const slot = readSlot(block);
      if (slot && overrides.has(slot)) {
        next = { ...next, props: { ...(next.props ?? {}), ...overrides.get(slot) } };
      }
      if (Array.isArray(next.children)) {
        next = { ...next, children: applyOverrides(next.children) };
      }
      return next;
    });

  return applyOverrides(current);
}
